import React from "react";
import {
    BLACK,
    BORDER,
    CARD_BG,
    FONT,
    GOLD_DARK,
    GOLD_TINT,
    TEXT_FAINT,
    TEXT_MUTED,
} from "./theme";
import { RmnWizardCategory, WizardObjective, WizardProductCategory, WizardProductRef } from "./types";
import { InlineProductSelector, Product } from "../../pages/videoProduction/components/InlineProductSelector";
import { rmnProductsEndpoint } from "../../api/endpoints";
import buildTrustArt from "../../assets/svg/objectives/build-trust-credibility.svg";
import educateArt from "../../assets/svg/objectives/educate-complex-products.svg";
import helpShoppersArt from "../../assets/svg/objectives/help-shoppers-choose.svg";
import retailMediaArt from "../../assets/svg/objectives/improve-retail-media-performance.svg";
import conversionsArt from "../../assets/svg/objectives/increase-conversions.svg";
import launchArt from "../../assets/svg/objectives/launch-new-product.svg";
import omnichannelArt from "../../assets/svg/objectives/maximize-omnichannel-performance.svg";
import reduceReturnsArt from "../../assets/svg/objectives/reduce-returns.svg";

// Branded objective graphics, keyed by objectives.slug from the API config.
const OBJECTIVE_ART: Record<string, string> = {
    "build-trust-credibility": buildTrustArt,
    "educate-complex-products": educateArt,
    "help-shoppers-choose": helpShoppersArt,
    "improve-retail-media-performance": retailMediaArt,
    "increase-conversions": conversionsArt,
    "launch-new-product": launchArt,
    "maximize-omnichannel-performance": omnichannelArt,
    "reduce-returns": reduceReturnsArt,
};

// Emoji fallback for objectives without artwork, keyed by objectives.icon.
const OBJECTIVE_ICONS: Record<string, string> = {
    "trending-up": "📈",
    megaphone: "📣",
    bot: "🤖",
    "refresh-ccw": "🔄",
    star: "⭐",
    "graduation-cap": "🎓",
    rocket: "🚀",
    compass: "🧭",
    tv: "📺",
    globe: "🌐",
};

const ObjectiveArt = ({ objective, size }: { objective: WizardObjective; size: number }) => {
    const art = OBJECTIVE_ART[objective.slug];
    if (art) {
        return (
            <img
                src={art}
                alt=""
                width={size}
                height={size}
                style={{ display: "block", width: size, height: size }}
            />
        );
    }
    return (
        <span style={{ fontSize: size * 0.72, lineHeight: 1 }}>
            {OBJECTIVE_ICONS[objective.icon || ""] || "🎯"}
        </span>
    );
};

interface Step1Props {
    objectives: WizardObjective[];
    categories: WizardProductCategory[];
    categoryId: number | null;
    objectiveId: number | null;
    secondaryObjectiveId: number | null;
    onCategoryChange: (id: number | null) => void;
    onObjectiveChange: (id: number) => void;
    onSecondaryObjectiveChange: (id: number | null) => void;
    // RMN wizard only: crawled retailer categories shown as a first dropdown.
    // Picking one pre-selects the TDC category via its crawl-time mapping
    // (handled by the parent) — the shopper can still change either.
    rmnCategories?: RmnWizardCategory[];
    rmnCategoryId?: number | null;
    onRmnCategoryChange?: (id: number | null) => void;
    rmnName?: string | null;
    // Strategy wizard only (TDC-1499): optional multi-product picker over the
    // network catalog, discovered (not-yet-imported) rows included — the
    // server auto-imports those when the strategy is created.
    showProductPicker?: boolean;
    products?: WizardProductRef[];
    onProductsChange?: (products: WizardProductRef[]) => void;
}

const categoryLabelStyle: React.CSSProperties = {
    fontFamily: FONT,
    fontSize: 12.5,
    fontWeight: 600,
    color: BLACK,
    display: "block",
    marginBottom: 6,
};

const categorySelectStyle = (hasValue: boolean): React.CSSProperties => ({
    fontFamily: FONT,
    fontSize: 14,
    color: hasValue ? BLACK : TEXT_FAINT,
    width: "100%",
    maxWidth: 420,
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid ${BORDER}`,
    background: CARD_BG,
    marginBottom: 24,
});

// Roots are selectable group headers; children are indented beneath so
// either level can be picked. Shared by the TDC and retailer dropdowns.
const groupedOptions = (categories: Array<{ id: number; name: string; parent_id?: number | null }>) =>
    categories
        .filter((c) => c.parent_id == null)
        .map((root) => {
            const children = categories.filter((c) => c.parent_id === root.id);
            return (
                <React.Fragment key={root.id}>
                    <option value={root.id}>{root.name}</option>
                    {children.map((child) => (
                        <option key={child.id} value={child.id}>
                            {`   ${root.name} › ${child.name}`}
                        </option>
                    ))}
                </React.Fragment>
            );
        });

export function Step1Objective({
    objectives,
    categories,
    categoryId,
    objectiveId,
    secondaryObjectiveId,
    onCategoryChange,
    onObjectiveChange,
    onSecondaryObjectiveChange,
    rmnCategories,
    rmnCategoryId,
    onRmnCategoryChange,
    rmnName,
    showProductPicker,
    products,
    onProductsChange,
}: Step1Props) {
    // Selector rows → wizard refs (rows without a kind are imported desires).
    const handleProductsChange = (list: Product[]) => {
        if (!onProductsChange) return;
        onProductsChange(
            list.map((p) => ({
                kind: p.kind === "discovered" ? ("discovered" as const) : ("desire" as const),
                desireid: p.desireid != null ? Number(p.desireid) : undefined,
                id: p.id != null ? Number(p.id) : undefined,
                title: p.title,
                image: p.image,
                url: p.url,
            })),
        );
    };

    return (
        <div>
            <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 700, color: BLACK, margin: "0 0 6px" }}>
                What are you trying to accomplish?
            </h2>
            <p style={{ fontFamily: FONT, fontSize: 14, color: TEXT_MUTED, margin: "0 0 20px", lineHeight: 1.6 }}>
                Choose your product category and the goal that's most important right now.
            </p>

            {rmnCategories && rmnCategories.length > 0 && (
                <>
                    <label style={categoryLabelStyle}>
                        {`${rmnName || "Retailer"} category`}
                    </label>
                    <select
                        value={rmnCategoryId ?? ""}
                        onChange={(e) =>
                            onRmnCategoryChange &&
                            onRmnCategoryChange(e.target.value ? Number(e.target.value) : null)
                        }
                        style={categorySelectStyle(rmnCategoryId != null)}
                    >
                        <option value="">Select a category…</option>
                        {groupedOptions(rmnCategories)}
                    </select>
                </>
            )}

            <label style={categoryLabelStyle}>TDC category</label>
            <select
                value={categoryId ?? ""}
                onChange={(e) => onCategoryChange(e.target.value ? Number(e.target.value) : null)}
                style={categorySelectStyle(categoryId != null)}
            >
                <option value="">Select a category…</option>
                {groupedOptions(categories)}
            </select>

            {showProductPicker && onProductsChange && (
                <>
                    <label style={categoryLabelStyle}>Products (optional)</label>
                    <div style={{ maxWidth: 420, marginBottom: 24 }}>
                        <InlineProductSelector
                            setFieldValue={() => {}}
                            selectedProduct={null}
                            setSelectedProduct={() => {}}
                            selectedBrandId={undefined}
                            multiple
                            selectedProducts={products || []}
                            onProductsChange={handleProductsChange}
                            productsEndpoint={rmnProductsEndpoint}
                            allowAdd={false}
                            requireBrand={false}
                            extraFetchParams={{ include_discovered: 1 }}
                        />
                    </div>
                </>
            )}

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                    gap: 12,
                }}
            >
                {objectives.map((o) => {
                    const selected = o.id === objectiveId;
                    return (
                        <button
                            key={o.id}
                            type="button"
                            onClick={() => onObjectiveChange(o.id)}
                            style={{
                                fontFamily: FONT,
                                textAlign: "center",
                                background: selected ? GOLD_TINT : CARD_BG,
                                border: `1.5px solid ${selected ? GOLD_DARK : BORDER}`,
                                borderRadius: 14,
                                padding: "20px 14px 16px",
                                cursor: "pointer",
                                transition: "border-color .15s, background .15s",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    marginBottom: 10,
                                }}
                            >
                                <ObjectiveArt objective={o} size={56} />
                            </div>
                            <div style={{ fontSize: 16.5, fontWeight: 700, color: BLACK, marginBottom: 6 }}>
                                {o.name}
                            </div>
                            <div style={{ fontSize: 12.5, color: TEXT_MUTED, lineHeight: 1.5 }}>
                                {o.description}
                            </div>
                        </button>
                    );
                })}
            </div>

            {objectiveId != null && (
                <div style={{ marginTop: 26 }}>
                    <div
                        style={{
                            fontFamily: FONT,
                            fontSize: 14.5,
                            fontWeight: 700,
                            color: BLACK,
                            marginBottom: 2,
                        }}
                    >
                        Add a secondary goal{" "}
                        <span style={{ fontWeight: 500, color: TEXT_FAINT, fontSize: 12.5 }}>
                            (optional)
                        </span>
                    </div>
                    <p
                        style={{
                            fontFamily: FONT,
                            fontSize: 12.5,
                            color: TEXT_MUTED,
                            margin: "0 0 12px",
                            lineHeight: 1.5,
                        }}
                    >
                        A secondary goal fills gaps in the recommendation without doubling it —
                        your primary objective still drives the package.
                    </p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {objectives
                            .filter((o) => o.id !== objectiveId)
                            .map((o) => {
                                const selected = o.id === secondaryObjectiveId;
                                return (
                                    <button
                                        key={o.id}
                                        type="button"
                                        onClick={() =>
                                            onSecondaryObjectiveChange(selected ? null : o.id)
                                        }
                                        style={{
                                            fontFamily: FONT,
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 6,
                                            fontSize: 12.5,
                                            fontWeight: 600,
                                            color: selected ? "#fff" : BLACK,
                                            background: selected ? GOLD_DARK : CARD_BG,
                                            border: `1.5px solid ${selected ? GOLD_DARK : BORDER}`,
                                            borderRadius: 99,
                                            padding: "8px 14px",
                                            cursor: "pointer",
                                            transition: "border-color .15s, background .15s",
                                        }}
                                    >
                                        <ObjectiveArt objective={o} size={18} />
                                        {o.name}
                                        {selected && <span style={{ fontSize: 11 }}>✕</span>}
                                    </button>
                                );
                            })}
                    </div>
                </div>
            )}
        </div>
    );
}
